/* eslint-disable @typescript-eslint/brace-style */
import { useNavigate, useLocation } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import { Paged } from 'api/common/types/Page';
import VehiclesService from 'api/vehicles/VehiclesService';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import { VehicleSearchCriteria } from 'api/vehicles/models/VehicleSearchCriteria';
import UsersService from 'api/users/UsersService';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Logger from 'common/services/Logger';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import styles from './VehiclesScreen.module.scss';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import toast from 'react-hot-toast';
import { Button, Container, Dropdown, DropdownButton, Stack, Form, Card, Breadcrumb } from 'react-bootstrap';
import Loading from 'common/services/Loading';

type Props = {
};

const VehiclesScreen: React.FC<Props> = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [criteria, setCriteria] = useState<VehicleSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 'v.license_plate'
    });
    const [vehiclesPage, setVehiclesPage] = useState<Paged<VehicleDto>>();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVehiclesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['MY_VEHICLES_WRITE']);
    const [filters, setFilters] = useState({
        allIn: ''
    });
    const getData = async () => {
        try {
            Loading.show();
            const page = await VehiclesService.getList(criteria);
            setVehiclesPage(page);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get Vehicles list', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const renderTableActionCell = (row: VehicleDto) => {
        return <DropdownButton id="dropdown-basic-button" title="..." size="sm" variant="outline-secondary">
            <Dropdown.Item onClick={() => navigate(`/vehicle/details/${row.id}`)}>{t('common.details')}</Dropdown.Item>
            { hasVehiclesWritePolicy && <>
                <Dropdown.Item onClick={() => navigate(`/vehicle/edit/${row.id}`)}>{t('common.edit')}</Dropdown.Item>
                <Dropdown.Item onClick={() => showRemoveItemDialog(row)}>{t('common.delete')}</Dropdown.Item></>
            }
        </DropdownButton>
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<VehicleDto>[] = [
        {
            field: 'licensePlate',
            name: t('vehicles.list.license_plate') ?? '',
            onSearch: onTableFilter,
            searchField: 'v.license_Plate'
        },
        {
            field: 'designation',
            name: t('vehicles.list.designation') ?? '',
            onSearch: onTableFilter,
            searchField: 'v.designation'
        },
        {
            field: 'deviceCode',
            name: t('vehicles.list.device') ?? '',
            onSearch: onTableFilter,
            searchField: 'd.code'
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        },
    ];

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<VehicleDto | null>(null);

    const showRemoveItemDialog = async (item: VehicleDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onRowClick = (event: any, id?: string) => {
        if (id) {
            if (event.ctrlKey) {
                window.open(location.pathname + '/details/' + id, '_blank');
                return;
            }
            navigate(`/vehicle/details/${id}`);
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await VehiclesService.remove(itemToRemove);
            onCancelRemove();
            toast.success(t('messages.record_delete_success'));
            setCriteria({ ...criteria, page: 1 })
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete vehicles', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [criteria]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFilters({ ...filters, [key]: value });
    }
    function clearFilters() {
        setFilters({ allIn: '' });
    }
    const onSearch = () => {
        setCriteria({ ...criteria, allIn: filters.allIn });
    }

    return (
        <ScreenTitle title={t('vehicles.title')}>
            <Breadcrumb>
                <Breadcrumb.Item active>{t('vehicles.title')}</Breadcrumb.Item>
            </Breadcrumb>
            <Container fluid>
                <Card className={styles.filters}>
                    <Card.Body>
                        <Stack direction="horizontal" gap={3} >
                            <Form.Control type="text" value={filters.allIn} className="me-auto" name="allIn" onChange={handleChange} placeholder={t('common.write_here')} />
                            <Button variant="secondary" onClick={onSearch}>{t('common.search')}</Button>
                            <div className="vr" />
                            <Button variant="outline-danger" onClick={clearFilters}>{t('common.clear')}</Button>
                            <Button variant="primary" onClick={() => navigate('/vehicle/new/')}>{t('common.new')}</Button>
                        </Stack>
                    </Card.Body>
                </Card>
            </Container>
            <Container fluid>
                <ListingTable
                    columns={tableColumns}
                    rows={vehiclesPage?.items || []}
                    onRowClick={(row, _, event) => onRowClick(event, row.id)}
                    allowHover={true}
                    initialSearch={{ colField: 'c.code', isOrderAsc: true }}
                />
                <PaginationWithInfo
                    itemName={t('vehicles.title')}
                    currentPage={vehiclesPage?.currentPage ?? 1}
                    pageItems={vehiclesPage?.items.length || 0}
                    totalItems={vehiclesPage?.totalItems || 0}
                    onChange={page => setCriteria({ ...criteria, page })}
                />
            </Container>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: itemToRemove?.licensePlate ?? '' })} />
        </ScreenTitle>
    );
};

export default VehiclesScreen;
